/* eslint-disable no-extra-parens */
/* eslint-disable no-fallthrough */
import { baseClasses } from '@weatherbug/common';
import { 
    TOGGLE_MOBILE_HOURLY_CARD_DETAILS, 
    GET_NEXT_12_HOURS, 
    GetNext12HoursForecastType, 
    ToggleMobileHourlyCardDetailsActionType,
    GOTO_DAYS_HOURLY_FORECAST_CARD,
    GotoDaysHourlyForecastCardType,
    RESET_HOURLY_FORECAST_DATA,
    ResetHourlyForecastDataActionType,
    LastHourCardLoadStatusType,
    SET_LAST_HOUR_CARD_INDEX_LOADED,
    SELECT_HOURLY_FORECAST_CHART,
    SelectHourylyForecastChartType } from '../actions/hourlyForecastPageViewActions';
import { HourlyForecastChartTypes } from '../../components/forecasts/common/types';
import type { ToggleMobileHourlyCardDetailsType, HourlyForecastPeriodCompact, HourlyForecastPeriodFromServer } from '../../components/forecasts/common/types';

const { AsyncAction } = baseClasses;

export const GET_FULL_HOURLY_FORECAST_V6 = new AsyncAction('GET_FULL_HOURLY_FORECAST_V6');

const transformServerVerboseHourlyForecastToClientData = (serverHourlyForecastPeriods: HourlyForecastPeriodFromServer[]): HourlyForecastPeriodCompact[] => {
    if (Array.isArray(serverHourlyForecastPeriods) && serverHourlyForecastPeriods?.length > 0) {
        const clientInitialHourlyForecastPeriods = serverHourlyForecastPeriods.map(serverHourlyForecastPeriod => {
            const clientHourlyForecastPeriod: HourlyForecastPeriodCompact = {
                fdtolcl: serverHourlyForecastPeriod.ForecastDateTimeOffsetLcl,
                ic: serverHourlyForecastPeriod.IconCode,
                t: serverHourlyForecastPeriod.Temperature,
                ccp: serverHourlyForecastPeriod.CloudCoverPercent100,
                d: serverHourlyForecastPeriod.Description,
                dpt: serverHourlyForecastPeriod.DewPointTemperature,
                flt: serverHourlyForecastPeriod.FeelsLikeTemperature,
                pt: serverHourlyForecastPeriod.PrecipType,
                pp: serverHourlyForecastPeriod.PrecipProbabilityPercent100,
                app: serverHourlyForecastPeriod.AdjustedPrecipProbabilityPercent100,
                pr: serverHourlyForecastPeriod.PrecipRate,
                rh: serverHourlyForecastPeriod.RelativeHumidityPercent100,
                tp: serverHourlyForecastPeriod.ThunderstormProbabilityPercent100,
                wd: serverHourlyForecastPeriod.WindDirection,
                ws: serverHourlyForecastPeriod.WindSpeed,
                sp: serverHourlyForecastPeriod.SurfacePressure,
                sr: serverHourlyForecastPeriod.SnowRate,
                sd: serverHourlyForecastPeriod.SnowDepth,
                gt: serverHourlyForecastPeriod.GlobeTemperature,
                wbt: serverHourlyForecastPeriod.WetBulbTemperature
            };
            return clientHourlyForecastPeriod;
        });
        
        // eslint-disable-next-line dot-notation
        (window as any)._AppData['shouldUseServerHourlyForecastData'] = true;

        return clientInitialHourlyForecastPeriods;
    }
    return [];
};

const initialHourlyForecastPeriods = transformServerVerboseHourlyForecastToClientData((window as any)._AppData?.HourlyForecastPeriods?.[0]);

export type HourlyForecastPageStateType = {
    selectedCardsRecord: ToggleMobileHourlyCardDetailsType;
    hourlyForecastPeriods?: HourlyForecastPeriodCompact[];
    apiHourlyForecastPeriods?: HourlyForecastPeriodCompact[];
    autoScrollToDaysHourCardIndex?: number;
    lastHourCardIndex: number;
    selectedHourlyForecastChart: keyof typeof HourlyForecastChartTypes;
}
const initialState: HourlyForecastPageStateType = {
    selectedCardsRecord: {},
    hourlyForecastPeriods: initialHourlyForecastPeriods.slice(0, 12),
    apiHourlyForecastPeriods: initialHourlyForecastPeriods,
    autoScrollToDaysHourCardIndex: 0,
    lastHourCardIndex: 0,
    selectedHourlyForecastChart: HourlyForecastChartTypes.precip
};

type HourlyForecastPageActionTypes = | 
    GetNext12HoursForecastType | 
    ToggleMobileHourlyCardDetailsActionType | 
    GotoDaysHourlyForecastCardType |
    ResetHourlyForecastDataActionType |
    LastHourCardLoadStatusType |
    SelectHourylyForecastChartType;

export function hourlyForecastPage(state = initialState, action: HourlyForecastPageActionTypes): HourlyForecastPageStateType
{
    switch (action.type)
    {
        case TOGGLE_MOBILE_HOURLY_CARD_DETAILS:
        {
            return {
                ...state,
                selectedCardsRecord: action.payload
            };
        }
        case GET_NEXT_12_HOURS:
        {
            const { hourlyForecastPeriods, apiHourlyForecastPeriods } = state;
            if (hourlyForecastPeriods?.length && apiHourlyForecastPeriods?.length) {
                const next12HoursMinIndexRange = hourlyForecastPeriods?.length;
                const next12HoursMaxIndexRange = next12HoursMinIndexRange + 12;
                const next12HoursForecasts = apiHourlyForecastPeriods.slice(next12HoursMinIndexRange, next12HoursMaxIndexRange);
                const forecastPeriodsToDisplay = [...hourlyForecastPeriods, ...next12HoursForecasts];
                return {
                    ...state,
                    hourlyForecastPeriods: forecastPeriodsToDisplay,
                    lastHourCardIndex: forecastPeriodsToDisplay?.length - 1
                };
            }
        }
        case GET_FULL_HOURLY_FORECAST_V6.fulfilled:
        {
            const { hourlyForecastPeriods } = state;
            const { hfp } = action?.payload?.[0]?.hf as any || {};
            
            if (hfp?.length) {
                if (hourlyForecastPeriods?.length && hourlyForecastPeriods?.length > 3) {
                    // page had been opened and location changed or new data was polled, update based on how many cards user had expanded.
                    const updatedHourlyForecastPeriods = hfp.slice(0, hourlyForecastPeriods.length);
                    return {
                        ...state,
                        hourlyForecastPeriods: updatedHourlyForecastPeriods,
                        apiHourlyForecastPeriods: hfp
                    };
                } else {
                    // when page first loads and there is no hourly page specific data to display - defaults to display 12hour data.
                    const initialHourlyDisplayData = hfp?.slice(0, 12);
                    return {
                        ...state,
                        hourlyForecastPeriods: initialHourlyDisplayData,
                        apiHourlyForecastPeriods: hfp
                    };
                }
            }           
        }
        case RESET_HOURLY_FORECAST_DATA: 
        {
            return {
                ...state,
                hourlyForecastPeriods: [],
                autoScrollToDaysHourCardIndex: 0
            };
        }
        case GOTO_DAYS_HOURLY_FORECAST_CARD: 
        {
            const { apiHourlyForecastPeriods } = state;
            const { cardIndex, next12HoursMaxIndexRange } = action.payload;

            const updatedHourlyForecastPeriods = apiHourlyForecastPeriods && next12HoursMaxIndexRange <= apiHourlyForecastPeriods?.length && cardIndex > 0 
                ? apiHourlyForecastPeriods?.slice(0, next12HoursMaxIndexRange)
                : apiHourlyForecastPeriods?.slice(0, 12); // default
            return {
                ...state,
                hourlyForecastPeriods: updatedHourlyForecastPeriods,
                autoScrollToDaysHourCardIndex: cardIndex
            };
        }
        case SET_LAST_HOUR_CARD_INDEX_LOADED:
        {
            return {
                ...state,
                lastHourCardIndex: action.payload
            };
        }
        case SELECT_HOURLY_FORECAST_CHART:
        {
            return {
                ...state,
                selectedHourlyForecastChart: action.payload
            };
        }
        default: 
        {
            return state;
        }
    }
}